import { type PropsWithChildren, useContext, createContext } from 'react';

import type {
  CategoricalBarChartConfiguration,
  CategoricalBarChartProps,
  RequiredCategoricalBarChartConfiguration,
} from '../types/categorical-bar-chart.js';
import {
  buildCategoricalBarChartConfig,
  deserializeConfig,
} from '../utils/categorical-config.js';
import { iterateSlots } from '../utils/iterate-slots.js';

/**
 * @internal
 */
export const CategoricalBarChartConfigContext = createContext<
  CategoricalBarChartConfiguration | string
>({});

/**
 * Provider for the CategoricalBarChart API config that can be shared across charts
 * @public
 */
export const CategoricalBarChartConfig =
  CategoricalBarChartConfigContext.Provider;

/**
 * @internal
 */
export const useCategoricalBarChartConfig = (
  props: PropsWithChildren<CategoricalBarChartProps>,
): RequiredCategoricalBarChartConfiguration => {
  const { children } = props;
  const contextConfig = useContext(CategoricalBarChartConfigContext);
  const slots = iterateSlots(children);
  const isImportedConfig = typeof contextConfig === 'string';

  const config = isImportedConfig
    ? deserializeConfig(contextConfig)
    : contextConfig;

  return buildCategoricalBarChartConfig(props, config, slots);
};
